import React, { useState } from 'react';
import { ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Aide } from '@/components/ui/aide';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { useEstMobile } from '@/hooks/useEstMobile';

/**
 * Une file d'attente qui ne doit pas écraser l'écran: UNE ligne, quel que soit
 * le nombre d'éléments — l'icône, le titre, le nombre, le motif — et un clic
 * ouvre un volet avec la liste complète, défilante dans un volet.
 *
 * Elle existe parce que ces blocs (camions à solder, stations à réapprovisionner…)
 * s'étiraient au-dessus de la liste de travail: à dix éléments la page ne montrait
 * plus que la file, et la liste qui compte descendait sous la ligne de flottaison.
 *
 * - `elements`: la liste entière: le volet la montre en entier et défile (pas de « page suivante »
 *   dans un volet: on lit, on agit, on ferme).
 * - `renderListe(lignes, fermer)`: rend toute la liste (un `<ul>` complet);
 *   `fermer()` referme le volet, à appeler avant d'ouvrir une fenêtre d'action.
 * - `pied`: contenu sous la liste (ex. propositions), dans le volet.
 * - `aide`: texte d'aide du volet, derrière l'icône Aide.
 */
export function BandeauFile({
  icone: Icone,
  titre,
  nombre,
  motif,
  detail,
  ton = 'warning',
  nom = 'éléments',
  elements,
  renderListe,
  pied = null,
  aide = null,
  className,
  testid,
}) {
  const [ouvert, setOuvert] = useState(false);
  const mobile = useEstMobile();
  const total = nombre ?? elements.length;
  if (!total) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOuvert(true)}
        aria-haspopup="dialog"
        aria-label={`${titre} : ${total} ${nom}. Ouvrir la liste`}
        data-testid={testid}
        className={cn(
          'group flex w-full items-center gap-3 rounded-xl border border-border/70 bg-card px-4 py-2.5 text-left shadow-sm transition-colors',
          'hover:border-primary/30 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
      >
        {Icone ? (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Icone className="h-4 w-4" aria-hidden="true" />
          </span>
        ) : null}
        <span className="shrink-0 text-sm font-bold text-foreground">{titre}</span>
        <Badge variant={ton} className="shrink-0 tabular-nums">
          {total}
        </Badge>
        {motif ? <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{motif}</span> : <span className="flex-1" />}
        {detail ? <span className="hidden shrink-0 text-sm tabular-nums text-muted-foreground md:inline">{detail}</span> : null}
        <span className="flex shrink-0 items-center gap-0.5 text-sm font-semibold text-primary">
          <span className="hidden sm:inline">Voir</span>
          <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
        </span>
      </button>

      <Sheet open={ouvert} onOpenChange={setOuvert}>
        <SheetContent
          side={mobile ? 'bottom' : 'right'}
          className={cn(
            'flex flex-col gap-0 p-0',
            mobile ? 'h-[92dvh] rounded-t-2xl' : 'w-full max-w-xl sm:max-w-xl',
          )}
        >
          <div className="shrink-0 border-b border-border px-4 py-3 pr-12">
            <SheetTitle className="flex items-center gap-2 text-base">
              {titre}
              <Badge variant={ton} className="tabular-nums">
                {total}
              </Badge>
              {aide ? <Aide titre={titre}>{aide}</Aide> : null}
            </SheetTitle>
            <SheetDescription className={motif ? 'text-xs' : 'sr-only'}>{motif || titre}</SheetDescription>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
            {renderListe(elements, () => setOuvert(false))}
            {pied}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default BandeauFile;
